// Role illustrations — one bold iconic glyph per role.
//
// Design rules (kept consistent across all roles):
//   - Single dominant shape, centered.
//   - Use `accent` for the primary fill, ink-black for inset detail.
//   - No type, no embedded text — labels live on the card frame, not the art.
//   - Designed to read at 50px square.
//   - Roles without a custom illustration fall back to a generic glyph
//     formed from the role number + faction-tinted disc.

import type { RoleConfig } from '../../../shared/src/types.js';

interface Props {
  id: string;
  accent?: string;
  // Kept for API compatibility with the previous illustrations.
  sub?: string;
  // For the generic fallback path: caller may pass the full role to read
  // .number / .latin without an extra lookup.
  role?: RoleConfig;
}

export function RoleArt({ id, accent = '#fff', role }: Props) {
  const c = {
    width: '100%',
    height: '100%',
    viewBox: '0 0 200 260',
    preserveAspectRatio: 'xMidYMid meet' as const,
  };
  const ink = '#0a0a0c';

  switch (id) {
    // ── TOWN (originals) ──
    case 'civilian':
      return (
        <svg {...c}>
          <rect x="40" y="100" width="120" height="14" fill={ink} />
          <path d="M48 100 Q100 50 152 100 Z" fill={accent} stroke={ink} strokeWidth="3" />
          <ellipse cx="100" cy="146" rx="38" ry="40" fill={accent} stroke={ink} strokeWidth="3" />
          <path d="M50 200 Q100 178 150 200 L160 250 L40 250 Z" fill={accent} stroke={ink} strokeWidth="3" />
          <line x1="100" y1="184" x2="100" y2="250" stroke={ink} strokeWidth="3" />
        </svg>
      );
    case 'sheriff':
      return (
        <svg {...c}>
          <g transform="translate(100 130)">
            <polygon
              points="0,-86 22,-26 84,-26 35,12 53,72 0,34 -53,72 -35,12 -84,-26 -22,-26"
              fill={accent} stroke={ink} strokeWidth="4" strokeLinejoin="miter"
            />
            <circle r="14" fill={ink} />
            <circle r="6" fill={accent} />
          </g>
        </svg>
      );
    case 'doctor':
      // Solid disc with a CREAM cross cut through it. Using ink for the
      // cross (the previous version) collapsed against an ink-filled disc
      // when vellum forces accent=ink and made the cross invisible.
      return (
        <svg {...c}>
          <circle cx="100" cy="130" r="78" fill={accent} />
          <rect x="84" y="64"  width="32" height="132" fill="#f4ead4" />
          <rect x="34" y="114" width="132" height="32" fill="#f4ead4" />
        </svg>
      );
    case 'lover':
      // Butterfly — symmetric wings with slim body and antennae.
      return (
        <svg {...c}>
          <path d="M100 80 Q 78 52 58 44" fill="none" stroke={ink} strokeWidth="3" strokeLinecap="round" />
          <path d="M100 80 Q 122 52 142 44" fill="none" stroke={ink} strokeWidth="3" strokeLinecap="round" />
          <circle cx="58" cy="44" r="4" fill={ink} />
          <circle cx="142" cy="44" r="4" fill={ink} />
          <path d="M100 92 Q 28 56 18 116 Q 26 156 96 138 Z" fill={accent} stroke={ink} strokeWidth="3" strokeLinejoin="round" />
          <path d="M100 92 Q 172 56 182 116 Q 174 156 104 138 Z" fill={accent} stroke={ink} strokeWidth="3" strokeLinejoin="round" />
          <path d="M96 142 Q 44 156 50 210 Q 88 220 100 178 Z" fill={accent} stroke={ink} strokeWidth="3" strokeLinejoin="round" />
          <path d="M104 142 Q 156 156 150 210 Q 112 220 100 178 Z" fill={accent} stroke={ink} strokeWidth="3" strokeLinejoin="round" />
          <ellipse cx="100" cy="140" rx="7" ry="58" fill={ink} />
          <circle cx="100" cy="86" r="9" fill={ink} />
        </svg>
      );
    case 'lawyer':
      return (
        <svg {...c}>
          <rect x="98" y="60" width="6" height="160" fill={accent} stroke={ink} strokeWidth="2" />
          <rect x="32" y="78" width="136" height="6" fill={accent} stroke={ink} strokeWidth="2" />
          <line x1="58" y1="84" x2="58" y2="120" stroke={ink} strokeWidth="3" />
          <line x1="142" y1="84" x2="142" y2="120" stroke={ink} strokeWidth="3" />
          <path d="M28 120 L88 120 L58 158 Z" fill={accent} stroke={ink} strokeWidth="3" strokeLinejoin="miter" />
          <path d="M112 120 L172 120 L142 158 Z" fill={accent} stroke={ink} strokeWidth="3" strokeLinejoin="miter" />
          <rect x="78" y="216" width="44" height="10" fill={accent} stroke={ink} strokeWidth="2" />
          <circle cx="100" cy="60" r="6" fill={accent} stroke={ink} strokeWidth="2" />
        </svg>
      );
    case 'bum':
      return (
        <svg {...c}>
          <g transform="translate(100 140) rotate(-10)">
            <path d="M-14 -78 L14 -78 L14 -50 Q34 -38 34 -16 L34 70 Q34 82 22 82 L-22 82 Q-34 82 -34 70 L-34 -16 Q-34 -38 -14 -50 Z" fill={accent} stroke={ink} strokeWidth="4" />
            <rect x="-14" y="-90" width="28" height="14" fill={ink} />
            <rect x="-26" y="0" width="52" height="42" fill={ink} />
          </g>
        </svg>
      );

    // ── TOWN (new) ──
    case 'bodyguard':
      // Heater shield with a cross.
      return (
        <svg {...c}>
          <path d="M100 40 L160 64 L160 138 Q160 196 100 230 Q40 196 40 138 L40 64 Z" fill={accent} stroke={ink} strokeWidth="4" strokeLinejoin="round" />
          <rect x="92" y="78" width="16" height="106" fill={ink} />
          <rect x="58" y="120" width="84" height="16" fill={ink} />
        </svg>
      );
    case 'mayor':
      // Top hat + ribbon.
      return (
        <svg {...c}>
          <rect x="46" y="170" width="108" height="14" fill={accent} stroke={ink} strokeWidth="3" />
          <rect x="60" y="60" width="80" height="120" fill={accent} stroke={ink} strokeWidth="4" />
          <rect x="60" y="140" width="80" height="14" fill={ink} />
          <circle cx="76" cy="100" r="6" fill={ink} />
        </svg>
      );
    case 'vigilante':
      // Pistol — short, angled.
      return (
        <svg {...c}>
          <g transform="translate(100 130)">
            <rect x="-30" y="-20" width="74" height="20" fill={accent} stroke={ink} strokeWidth="3" />
            <path d="M-30 0 L-30 30 L0 30 L0 0 Z" fill={accent} stroke={ink} strokeWidth="3" />
            <path d="M-46 0 L-30 0 L-22 50 L-50 50 Z" fill={accent} stroke={ink} strokeWidth="3" />
            <rect x="44" y="-22" width="6" height="6" fill={ink} />
          </g>
        </svg>
      );
    case 'medium':
      // Crystal ball on a base.
      return (
        <svg {...c}>
          <circle cx="100" cy="120" r="60" fill={accent} stroke={ink} strokeWidth="4" />
          <ellipse cx="84" cy="100" rx="20" ry="12" fill="#fff" opacity="0.4" />
          <path d="M50 188 L150 188 L160 220 L40 220 Z" fill={accent} stroke={ink} strokeWidth="3" />
        </svg>
      );
    case 'investigator':
      // Magnifying glass.
      return (
        <svg {...c}>
          <circle cx="86" cy="116" r="50" fill="none" stroke={accent} strokeWidth="14" />
          <line x1="124" y1="156" x2="170" y2="210" stroke={accent} strokeWidth="20" strokeLinecap="round" />
          <circle cx="86" cy="116" r="42" fill={accent} opacity="0.18" />
        </svg>
      );
    case 'bus_driver':
      // Steering wheel — bold ring + central hub + four spokes that actually
      // connect the hub to the rim.
      return (
        <svg {...c}>
          <circle cx="100" cy="130" r="76" fill="none" stroke={accent} strokeWidth="14" />
          <rect x="94" y="60"  width="12" height="60" fill={accent} />
          <rect x="94" y="140" width="12" height="60" fill={accent} />
          <rect x="30"  y="124" width="60" height="12" fill={accent} />
          <rect x="110" y="124" width="60" height="12" fill={accent} />
          <circle cx="100" cy="130" r="22" fill={accent} />
        </svg>
      );
    case 'veteran':
      // Combat helmet seen straight on — single silhouette with a narrow
      // chinstrap notch. No floating ornaments below; the previous icon had
      // an unconnected medal that read as a typo against the helmet.
      return (
        <svg {...c}>
          <path
            d="M44 154 Q44 76 100 70 Q156 76 156 154 L156 168 Q156 184 140 184 L60 184 Q44 184 44 168 Z"
            fill={accent}
          />
          {/* Five-point star centred on the brow — the only bright detail,
              kept on a separate fill so it reads against the silhouette. */}
          <polygon
            points="100,98 107,118 128,118 111,131 117,151 100,140 83,151 89,131 72,118 93,118"
            fill="#f4ead4"
          />
          {/* Chinstrap on the side of the helmet, biting into the dome. */}
          <rect x="44" y="170" width="112" height="6" fill="#f4ead4" opacity="0.6" />
        </svg>
      );
    case 'spy':
      // Stylised eye.
      return (
        <svg {...c}>
          <path d="M30 130 Q100 60 170 130 Q100 200 30 130 Z" fill={accent} stroke={ink} strokeWidth="4" />
          <circle cx="100" cy="130" r="28" fill={ink} />
          <circle cx="108" cy="122" r="6" fill={accent} />
        </svg>
      );
    case 'watcher':
      // Binoculars.
      return (
        <svg {...c}>
          <rect x="36" y="98" width="56" height="64" rx="8" fill={accent} stroke={ink} strokeWidth="3" />
          <rect x="108" y="98" width="56" height="64" rx="8" fill={accent} stroke={ink} strokeWidth="3" />
          <rect x="92" y="118" width="16" height="20" fill={accent} stroke={ink} strokeWidth="3" />
          <circle cx="64" cy="130" r="14" fill={ink} />
          <circle cx="136" cy="130" r="14" fill={ink} />
        </svg>
      );
    case 'priest':
      // Cross with halo.
      return (
        <svg {...c}>
          <circle cx="100" cy="78" r="34" fill="none" stroke={accent} strokeWidth="6" />
          <rect x="92" y="80" width="16" height="160" fill={accent} stroke={ink} strokeWidth="3" />
          <rect x="50" y="124" width="100" height="16" fill={accent} stroke={ink} strokeWidth="3" />
        </svg>
      );
    case 'detective':
      // Fedora with hatband.
      return (
        <svg {...c}>
          <ellipse cx="100" cy="184" rx="86" ry="14" fill={accent} stroke={ink} strokeWidth="3" />
          <path d="M40 184 Q40 86 100 78 Q160 86 160 184 Z" fill={accent} stroke={ink} strokeWidth="4" />
          <rect x="40" y="162" width="120" height="16" fill={ink} />
          <rect x="124" y="162" width="14" height="16" fill={accent} />
        </svg>
      );
    case 'escort':
      // Martini glass with olive.
      return (
        <svg {...c}>
          <path d="M38 78 L162 78 L100 162 Z" fill={accent} stroke={ink} strokeWidth="4" strokeLinejoin="round" />
          <rect x="96" y="160" width="8" height="60" fill={accent} stroke={ink} strokeWidth="2" />
          <rect x="60" y="218" width="80" height="14" rx="2" fill={accent} stroke={ink} strokeWidth="3" />
          <circle cx="120" cy="104" r="11" fill={ink} />
          <circle cx="117" cy="101" r="3" fill={accent} />
        </svg>
      );
    case 'monk':
      // Hooded figure with chest cross.
      return (
        <svg {...c}>
          <path d="M50 232 Q50 102 100 78 Q150 102 150 232 Z" fill={accent} stroke={ink} strokeWidth="4" strokeLinejoin="round" />
          <ellipse cx="100" cy="130" rx="26" ry="32" fill={ink} />
          <rect x="96" y="172" width="8" height="40" fill={ink} />
          <rect x="84" y="184" width="32" height="8" fill={ink} />
        </svg>
      );
    case 'coroner':
      // Toe tag (pentagon) with eyelet and cross marking.
      return (
        <svg {...c}>
          <path d="M64 56 L100 36 L136 56 L150 222 L50 222 Z" fill={accent} stroke={ink} strokeWidth="4" strokeLinejoin="round" />
          <circle cx="100" cy="56" r="8" fill={ink} />
          <rect x="92" y="120" width="16" height="68" fill={ink} />
          <rect x="72" y="142" width="56" height="16" fill={ink} />
        </svg>
      );

    // ── MAFIA (originals) ──
    case 'mafia':
      return (
        <svg {...c}>
          <g transform="translate(100 132)">
            <circle cx="-22" cy="-4" r="32" fill={accent} stroke={ink} strokeWidth="4" />
            <circle cx="-22" cy="-4" r="10" fill={ink} />
            <rect x="6" y="-12" width="58" height="18" fill={accent} stroke={ink} strokeWidth="3" />
            <rect x="58" y="-16" width="8" height="6" fill={ink} />
            <path d="M-8 14 L-8 24 L4 24 Q14 24 14 14 L14 6 L-8 6 Z" fill={accent} stroke={ink} strokeWidth="3" />
            <path d="M-32 14 L-12 14 L-2 56 L-46 56 Z" fill={accent} stroke={ink} strokeWidth="3" />
          </g>
        </svg>
      );
    case 'don':
      return (
        <svg {...c}>
          <g transform="translate(100 138)">
            <path d="M-72 -10 L-58 -64 L-30 -22 L0 -76 L30 -22 L58 -64 L72 -10 Z" fill={accent} stroke={ink} strokeWidth="4" strokeLinejoin="round" />
            <rect x="-72" y="-10" width="144" height="22" fill={accent} stroke={ink} strokeWidth="4" />
            <circle cx="-58" cy="-64" r="7" fill={ink} />
            <circle cx="0" cy="-76" r="9" fill={ink} />
            <circle cx="58" cy="-64" r="7" fill={ink} />
          </g>
        </svg>
      );

    // ── MAFIA (new) ──
    case 'goon':
      // Brass knuckles.
      return (
        <svg {...c}>
          <g transform="translate(100 130)">
            <circle cx="-48" r="22" fill={accent} stroke={ink} strokeWidth="3" />
            <circle cx="-16" r="22" fill={accent} stroke={ink} strokeWidth="3" />
            <circle cx="16" r="22" fill={accent} stroke={ink} strokeWidth="3" />
            <circle cx="48" r="22" fill={accent} stroke={ink} strokeWidth="3" />
            <circle cx="-48" r="10" fill={ink} />
            <circle cx="-16" r="10" fill={ink} />
            <circle cx="16" r="10" fill={ink} />
            <circle cx="48" r="10" fill={ink} />
            <rect x="-66" y="22" width="132" height="14" fill={accent} stroke={ink} strokeWidth="3" />
          </g>
        </svg>
      );
    case 'consigliere':
      // Folder with seal.
      return (
        <svg {...c}>
          <rect x="34" y="80" width="132" height="120" fill={accent} stroke={ink} strokeWidth="4" />
          <path d="M34 80 L80 80 L88 96 L166 96 L166 80 Z" fill={ink} opacity="0.3" />
          <circle cx="100" cy="148" r="22" fill={ink} />
          <circle cx="100" cy="148" r="10" fill={accent} />
        </svg>
      );
    case 'framer':
      // Frame around an X.
      return (
        <svg {...c}>
          <rect x="40" y="60" width="120" height="160" fill="none" stroke={accent} strokeWidth="14" />
          <line x1="60" y1="80" x2="140" y2="200" stroke={accent} strokeWidth="14" strokeLinecap="round" />
          <line x1="140" y1="80" x2="60" y2="200" stroke={accent} strokeWidth="14" strokeLinecap="round" />
        </svg>
      );
    case 'janitor':
      // Bucket + mop poking out at an angle. Bucket dominates so the icon
      // reads as a cleaning kit rather than a lampshade. The previous
      // version had a wide trapezoidal "mop head" that looked like a
      // lampshade on a stand.
      return (
        <svg {...c}>
          {/* Mop pole leaning out of the bucket, top-right */}
          <g transform="rotate(18 130 60)">
            <rect x="124" y="14" width="10" height="124" fill={accent} />
            {/* Mop puff — round soft shape, with cream stripes for fibres */}
            <ellipse cx="129" cy="20" rx="30" ry="20" fill={accent} />
            <line x1="118" y1="18" x2="116" y2="38" stroke="#f4ead4" strokeWidth="2.5" />
            <line x1="129" y1="14" x2="129" y2="40" stroke="#f4ead4" strokeWidth="2.5" />
            <line x1="140" y1="18" x2="142" y2="38" stroke="#f4ead4" strokeWidth="2.5" />
          </g>
          {/* Bucket — large, centered */}
          <path d="M40 138 L160 138 L150 224 L50 224 Z" fill={accent} />
          <rect x="34" y="132" width="132" height="12" fill={accent} />
          {/* Hollow opening (cream) shows it's a bucket */}
          <ellipse cx="100" cy="140" rx="58" ry="7" fill="#f4ead4" />
          {/* Wire handle arcing above the rim */}
          <path d="M52 134 Q100 96 148 134" fill="none" stroke={accent} strokeWidth="5" />
        </svg>
      );
    case 'hitman':
      // Crosshair.
      return (
        <svg {...c}>
          <circle cx="100" cy="130" r="74" fill="none" stroke={accent} strokeWidth="8" />
          <circle cx="100" cy="130" r="14" fill={accent} />
          <line x1="100" y1="40" x2="100" y2="100" stroke={accent} strokeWidth="6" />
          <line x1="100" y1="160" x2="100" y2="220" stroke={accent} strokeWidth="6" />
          <line x1="10" y1="130" x2="70" y2="130" stroke={accent} strokeWidth="6" />
          <line x1="130" y1="130" x2="190" y2="130" stroke={accent} strokeWidth="6" />
        </svg>
      );
    case 'mafia_lover':
      // Envelope (blackmail).
      return (
        <svg {...c}>
          <rect x="32" y="80" width="136" height="100" fill={accent} stroke={ink} strokeWidth="4" />
          <path d="M32 80 L100 144 L168 80" fill="none" stroke={ink} strokeWidth="3" />
          <circle cx="148" cy="160" r="14" fill={ink} />
          <circle cx="148" cy="160" r="6" fill={accent} />
        </svg>
      );
    case 'saboteur':
      // Bomb with sparking fuse.
      return (
        <svg {...c}>
          <circle cx="100" cy="160" r="62" fill={accent} stroke={ink} strokeWidth="4" />
          <path d="M100 98 Q124 70 144 78 Q156 84 162 56" fill="none" stroke={ink} strokeWidth="4" strokeLinecap="round" />
          <g transform="translate(162 56)">
            <line x1="-12" y1="0" x2="12" y2="0" stroke={accent} strokeWidth="4" strokeLinecap="round" />
            <line x1="0" y1="-12" x2="0" y2="12" stroke={accent} strokeWidth="4" strokeLinecap="round" />
            <line x1="-9" y1="-9" x2="9" y2="9" stroke={accent} strokeWidth="4" strokeLinecap="round" />
            <line x1="-9" y1="9" x2="9" y2="-9" stroke={accent} strokeWidth="4" strokeLinecap="round" />
          </g>
          <ellipse cx="78" cy="138" rx="14" ry="10" fill="#fff" opacity="0.25" />
        </svg>
      );
    case 'wheelman':
      // Side-view sports car.
      return (
        <svg {...c}>
          <path d="M22 188 L22 144 L52 100 L148 100 L178 144 L178 188 Z" fill={accent} stroke={ink} strokeWidth="4" strokeLinejoin="round" />
          <path d="M58 138 L82 110 L118 110 L142 138 Z" fill={ink} />
          <line x1="100" y1="110" x2="100" y2="138" stroke={accent} strokeWidth="3" />
          <circle cx="60" cy="190" r="22" fill={ink} stroke={accent} strokeWidth="4" />
          <circle cx="140" cy="190" r="22" fill={ink} stroke={accent} strokeWidth="4" />
          <circle cx="60" cy="190" r="8" fill={accent} />
          <circle cx="140" cy="190" r="8" fill={accent} />
        </svg>
      );
    case 'mole':
      // Mound with peeking eyes and motion lines.
      return (
        <svg {...c}>
          <path d="M40 110 L52 130 M70 92 L80 116 M124 92 L132 116 M152 108 L162 130" stroke={ink} strokeWidth="3" strokeLinecap="round" />
          <path d="M20 224 Q20 124 100 124 Q180 124 180 224 Z" fill={accent} stroke={ink} strokeWidth="4" strokeLinejoin="round" />
          <ellipse cx="100" cy="226" rx="44" ry="20" fill={ink} />
          <circle cx="86" cy="206" r="8" fill={accent} stroke={ink} strokeWidth="2" />
          <circle cx="114" cy="206" r="8" fill={accent} stroke={ink} strokeWidth="2" />
          <circle cx="86" cy="206" r="3" fill={ink} />
          <circle cx="114" cy="206" r="3" fill={ink} />
        </svg>
      );
    case 'capo':
      // Cigar with rising smoke.
      return (
        <svg {...c}>
          <path d="M50 100 Q40 70 56 50 Q72 30 60 10" stroke={accent} strokeWidth="4" fill="none" strokeLinecap="round" opacity="0.55" />
          <g transform="translate(100 144) rotate(-22)">
            <rect x="-72" y="-14" width="144" height="28" rx="4" fill={accent} stroke={ink} strokeWidth="3" />
            <rect x="44" y="-14" width="28" height="28" rx="2" fill={ink} />
            <rect x="-72" y="-14" width="18" height="28" fill="#ff8a4a" />
            <circle cx="-72" cy="0" r="8" fill="#fff3a6" />
          </g>
        </svg>
      );
    case 'snitch':
      // Old phone receiver (handset).
      return (
        <svg {...c}>
          <g transform="translate(100 132) rotate(-32)">
            <path d="M-78 -16 Q-78 -38 -56 -38 L-30 -38 L-22 -14 L22 -14 L30 -38 L56 -38 Q78 -38 78 -16 Q78 6 56 6 L-56 6 Q-78 6 -78 -16 Z" fill={accent} stroke={ink} strokeWidth="4" strokeLinejoin="round" />
            <circle cx="-58" cy="-22" r="8" fill={ink} />
            <circle cx="58" cy="-22" r="8" fill={ink} />
          </g>
        </svg>
      );

    // ── SOLO (originals) ──
    case 'arsonist':
      // Bonfire with tongues of flame leaping above crossed logs. Bigger
      // and more readable than the previous twin-blob silhouette, which
      // didn't reliably read as fire on the dark infernal background.
      return (
        <svg {...c}>
          {/* Outer flame */}
          <path
            d="M100 30 C72 64 84 96 96 122 C76 142 78 172 96 196 C82 218 92 240 100 244 C108 240 118 218 104 196 C122 172 124 142 104 122 C116 96 128 64 100 30 Z"
            fill={accent}
          />
          {/* Inner brighter core */}
          <path
            d="M100 78 C90 100 96 120 100 134 C92 150 92 170 100 188 C108 170 108 150 100 134 C104 120 110 100 100 78 Z"
            fill="#ffe6a8" opacity="0.85"
          />
          {/* Crossed logs at the base */}
          <rect x="44"  y="216" width="112" height="14" rx="3" fill={accent} transform="rotate(-12 100 223)" />
          <rect x="44"  y="216" width="112" height="14" rx="3" fill={accent} transform="rotate(12 100 223)" />
          {/* Spark above the flame */}
          <circle cx="100" cy="20" r="4" fill="#ffe6a8" />
        </svg>
      );
    case 'maniac':
      // Butcher's cleaver — wide rectangular blade with a wooden handle
      // and rivet detail. The previous icon was a thin upright dagger that
      // was easy to mistake for an exclamation mark on small renders.
      return (
        <svg {...c}>
          <g transform="translate(100 132) rotate(-12)">
            {/* Cleaver blade */}
            <path d="M-78 -54 L40 -54 L40 28 L-50 28 L-78 12 Z" fill={accent} />
            {/* Sharpening highlight along the bottom edge */}
            <path d="M-78 12 L-50 28 L40 28" fill="none" stroke="#fff" strokeWidth="2" opacity="0.45" />
            {/* Hole in the blade (classic butcher cleaver detail) */}
            <circle cx="20" cy="-32" r="6" fill="#0a0306" />
            {/* Wooden handle */}
            <rect x="40" y="-18" width="60" height="22" rx="2" fill="#3a1f12" stroke={accent} strokeWidth="1.5" />
            {/* Handle rivets */}
            <circle cx="56" cy="-7" r="3" fill={accent} />
            <circle cx="74" cy="-7" r="3" fill={accent} />
            <circle cx="92" cy="-7" r="3" fill={accent} />
            {/* Drip below the blade */}
            <path d="M-30 28 L-26 50 L-22 28 Z" fill={accent} />
          </g>
        </svg>
      );

    // ── SOLO (new) ──
    case 'werewolf':
      // Crescent moon + pawprint hint.
      return (
        <svg {...c}>
          <path d="M150 60 A80 80 0 1 0 150 200 A60 60 0 1 1 150 60 Z" fill={accent} stroke={ink} strokeWidth="4" />
          <circle cx="74" cy="120" r="8" fill={ink} />
          <circle cx="92" cy="100" r="8" fill={ink} />
          <circle cx="110" cy="100" r="8" fill={ink} />
          <circle cx="128" cy="120" r="8" fill={ink} />
          <ellipse cx="100" cy="146" rx="20" ry="14" fill={ink} />
        </svg>
      );
    case 'survivor':
      // Tactical vest with a centred trauma plate emblazoned with a medic
      // cross. The previous arrangement of cream pouches happened to align
      // visually as the letter "M", which broke the read entirely.
      return (
        <svg {...c}>
          {/* Vest body with armhole notches at the shoulders */}
          <path
            d="M40 84 Q40 70 56 70 L82 70 Q92 88 100 88 Q108 88 118 70 L144 70 Q160 70 160 84 L160 220 L40 220 Z"
            fill={accent}
          />
          {/* Trauma plate (cream) — main feature on the chest */}
          <rect x="64" y="106" width="72" height="86" fill="#f4ead4" />
          {/* Cross over the plate */}
          <rect x="92" y="118" width="16" height="62" fill={accent} />
          <rect x="76" y="142" width="48" height="16" fill={accent} />
          {/* Strap buckles on the sides */}
          <rect x="46" y="150" width="14" height="6" fill="#f4ead4" />
          <rect x="140" y="150" width="14" height="6" fill="#f4ead4" />
        </svg>
      );
    case 'jester':
      // Two-pronged jester hat with bells.
      return (
        <svg {...c}>
          <path d="M40 200 L100 60 L100 200 Z" fill={accent} stroke={ink} strokeWidth="4" strokeLinejoin="round" />
          <path d="M160 200 L100 60 L100 200 Z" fill={accent} stroke={ink} strokeWidth="4" strokeLinejoin="round" />
          <rect x="40" y="200" width="120" height="20" fill={ink} />
          <circle cx="40" cy="200" r="14" fill={accent} stroke={ink} strokeWidth="3" />
          <circle cx="160" cy="200" r="14" fill={accent} stroke={ink} strokeWidth="3" />
        </svg>
      );
    case 'witch':
      // Cauldron with bubbles.
      return (
        <svg {...c}>
          <path d="M40 130 Q40 200 100 220 Q160 200 160 130 Z" fill={accent} stroke={ink} strokeWidth="4" />
          <rect x="30" y="124" width="140" height="14" fill={ink} />
          <circle cx="80" cy="100" r="8" fill={accent} />
          <circle cx="106" cy="80" r="6" fill={accent} />
          <circle cx="124" cy="106" r="10" fill={accent} />
        </svg>
      );
    case 'executioner':
      // Hanging noose.
      return (
        <svg {...c}>
          <path d="M100 40 L100 140" stroke={accent} strokeWidth="6" />
          <circle cx="100" cy="170" r="32" fill="none" stroke={accent} strokeWidth="10" />
          <line x1="78" y1="148" x2="122" y2="148" stroke={accent} strokeWidth="6" />
        </svg>
      );

    // ── TOWN (extended) ──
    case 'psychic':
      // Crystal ball on a stand, with sparkle.
      return (
        <svg {...c}>
          <circle cx="100" cy="130" r="62" fill={accent} stroke={ink} strokeWidth="4" />
          <ellipse cx="80" cy="106" rx="18" ry="10" fill="#fff" opacity="0.4" />
          <path d="M62 198 L138 198 L160 230 L40 230 Z" fill={ink} stroke={ink} strokeWidth="3" strokeLinejoin="round" />
          <path d="M48 56 L52 70 L66 74 L52 78 L48 92 L44 78 L30 74 L44 70 Z" fill={accent} stroke={ink} strokeWidth="2" />
          <path d="M154 32 L156 42 L166 44 L156 46 L154 56 L152 46 L142 44 L152 42 Z" fill={accent} stroke={ink} strokeWidth="2" />
        </svg>
      );
    case 'bartender':
      // Martini glass with olive on a pick.
      return (
        <svg {...c}>
          <path d="M40 70 L160 70 L100 156 Z" fill={accent} stroke={ink} strokeWidth="4" strokeLinejoin="round" />
          <rect x="96" y="156" width="8" height="60" fill={accent} stroke={ink} strokeWidth="2" />
          <rect x="60" y="216" width="80" height="14" rx="2" fill={accent} stroke={ink} strokeWidth="3" />
          <line x1="118" y1="50" x2="86" y2="118" stroke={ink} strokeWidth="3" />
          <circle cx="86" cy="118" r="12" fill={ink} />
          <circle cx="86" cy="118" r="5" fill={accent} />
        </svg>
      );
    case 'trapper':
      // Bear trap — semicircle with teeth.
      return (
        <svg {...c}>
          <path d="M30 160 A70 70 0 0 1 170 160 Z" fill={accent} stroke={ink} strokeWidth="4" />
          <path d="M30 160 L46 140 L62 160 L78 140 L94 160 L110 140 L126 160 L142 140 L158 160 L170 160" fill={ink} />
          <rect x="46" y="160" width="108" height="16" fill={ink} />
          <circle cx="100" cy="200" r="14" fill={accent} stroke={ink} strokeWidth="3" />
          <circle cx="100" cy="200" r="5" fill={ink} />
        </svg>
      );
    case 'paramedic':
      // Bold medical cross with pulse line.
      return (
        <svg {...c}>
          <rect x="40" y="80" width="120" height="120" rx="10" fill={accent} stroke={ink} strokeWidth="4" />
          <rect x="86" y="100" width="28" height="80" fill={ink} />
          <rect x="60" y="126" width="80" height="28" fill={ink} />
          <path d="M30 224 L70 224 L82 204 L100 244 L118 204 L132 224 L170 224" fill="none" stroke={accent} strokeWidth="5" strokeLinejoin="miter" />
        </svg>
      );
    case 'scout':
      // Binoculars.
      return (
        <svg {...c}>
          <g transform="translate(100 132)">
            <rect x="-72" y="-30" width="56" height="64" rx="10" fill={accent} stroke={ink} strokeWidth="4" />
            <rect x="16" y="-30" width="56" height="64" rx="10" fill={accent} stroke={ink} strokeWidth="4" />
            <rect x="-16" y="-12" width="32" height="20" fill={accent} stroke={ink} strokeWidth="3" />
            <circle cx="-44" cy="2" r="14" fill={ink} />
            <circle cx="44" cy="2" r="14" fill={ink} />
            <circle cx="-44" cy="2" r="6" fill={accent} />
            <circle cx="44" cy="2" r="6" fill={accent} />
          </g>
        </svg>
      );
    case 'jailer':
      // Cell bars (top) plus a free-standing key (bottom) — the previous
      // icon overlapped the key onto the bars, which read as a typographic
      // glitch instead of a key.
      return (
        <svg {...c}>
          <rect x="36" y="48"  width="128" height="14" fill={accent} />
          <rect x="36" y="142" width="128" height="14" fill={accent} />
          <rect x="50"  y="62" width="14" height="80" fill={accent} />
          <rect x="93"  y="62" width="14" height="80" fill={accent} />
          <rect x="136" y="62" width="14" height="80" fill={accent} />
          {/* key, bow on the left, two teeth on the right */}
          <g transform="translate(60 200)">
            <circle cx="0" cy="0" r="22" fill="none" stroke={accent} strokeWidth="9" />
            <rect x="22" y="-5" width="76" height="10" fill={accent} />
            <rect x="78" y="5"  width="10" height="14" fill={accent} />
            <rect x="92" y="5"  width="10" height="14" fill={accent} />
          </g>
        </svg>
      );
    case 'tracker':
      // Two diagonal footprints.
      return (
        <svg {...c}>
          <g transform="translate(72 96) rotate(-12)">
            <ellipse cx="0" cy="0" rx="22" ry="32" fill={accent} stroke={ink} strokeWidth="3" />
            <circle cx="-14" cy="-40" r="6" fill={accent} stroke={ink} strokeWidth="2" />
            <circle cx="0" cy="-46" r="6" fill={accent} stroke={ink} strokeWidth="2" />
            <circle cx="14" cy="-40" r="6" fill={accent} stroke={ink} strokeWidth="2" />
          </g>
          <g transform="translate(132 168) rotate(-12)">
            <ellipse cx="0" cy="0" rx="22" ry="32" fill={accent} stroke={ink} strokeWidth="3" />
            <circle cx="-14" cy="-40" r="6" fill={accent} stroke={ink} strokeWidth="2" />
            <circle cx="0" cy="-46" r="6" fill={accent} stroke={ink} strokeWidth="2" />
            <circle cx="14" cy="-40" r="6" fill={accent} stroke={ink} strokeWidth="2" />
          </g>
        </svg>
      );

    // ── MAFIA (extended) ──
    case 'enforcer':
      // Clenched fist silhouette.
      return (
        <svg {...c}>
          <g transform="translate(100 132)">
            <path d="M-50 -40 Q-50 -64 -26 -64 L34 -64 Q58 -64 58 -40 L58 30 Q58 60 30 60 L-22 60 Q-50 60 -50 30 Z" fill={accent} stroke={ink} strokeWidth="4" strokeLinejoin="round" />
            <line x1="-50" y1="-22" x2="58" y2="-22" stroke={ink} strokeWidth="3" />
            <line x1="-50" y1="0" x2="58" y2="0" stroke={ink} strokeWidth="3" />
            <line x1="-50" y1="22" x2="58" y2="22" stroke={ink} strokeWidth="3" />
            <rect x="-72" y="-22" width="22" height="60" rx="6" fill={accent} stroke={ink} strokeWidth="4" />
          </g>
        </svg>
      );
    case 'strangler':
      // Rope with hangman knot.
      return (
        <svg {...c}>
          <path d="M60 50 Q140 80 100 130 Q60 170 140 200" fill="none" stroke={accent} strokeWidth="10" strokeLinecap="round" />
          <rect x="78" y="120" width="44" height="48" rx="8" fill={accent} stroke={ink} strokeWidth="4" />
          <line x1="78" y1="134" x2="122" y2="134" stroke={ink} strokeWidth="3" />
          <line x1="78" y1="148" x2="122" y2="148" stroke={ink} strokeWidth="3" />
          <line x1="78" y1="162" x2="122" y2="162" stroke={ink} strokeWidth="3" />
        </svg>
      );
    case 'disguiser':
      // Two overlapping theater masks.
      return (
        <svg {...c}>
          <g transform="translate(78 132)">
            <ellipse cx="0" cy="0" rx="42" ry="56" fill={accent} stroke={ink} strokeWidth="4" />
            <path d="M-22 -8 Q-14 -14 -6 -8 M6 -8 Q14 -14 22 -8" fill="none" stroke={ink} strokeWidth="3" strokeLinecap="round" />
            <path d="M-16 22 Q0 14 16 22" fill="none" stroke={ink} strokeWidth="3" strokeLinecap="round" />
          </g>
          <g transform="translate(126 132)">
            <ellipse cx="0" cy="0" rx="42" ry="56" fill={accent} stroke={ink} strokeWidth="4" opacity="0.7" />
            <path d="M-22 -8 L-6 -2 M6 -2 L22 -8" fill="none" stroke={ink} strokeWidth="3" strokeLinecap="round" />
            <path d="M-16 22 Q0 30 16 22" fill="none" stroke={ink} strokeWidth="3" strokeLinecap="round" />
          </g>
        </svg>
      );
    case 'forger':
      // Sealed document with wax stamp.
      return (
        <svg {...c}>
          <path d="M48 50 L138 50 L162 74 L162 220 L48 220 Z" fill={accent} stroke={ink} strokeWidth="4" strokeLinejoin="round" />
          <line x1="68" y1="92" x2="142" y2="92" stroke={ink} strokeWidth="3" />
          <line x1="68" y1="112" x2="142" y2="112" stroke={ink} strokeWidth="3" />
          <line x1="68" y1="132" x2="120" y2="132" stroke={ink} strokeWidth="3" />
          <circle cx="124" cy="180" r="22" fill={ink} stroke={accent} strokeWidth="4" />
          <path d="M114 180 L124 188 L138 172" fill="none" stroke={accent} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );

    // ── SOLO (extended) ──
    case 'cultist':
      // Pentagram inside a circle.
      return (
        <svg {...c}>
          <circle cx="100" cy="132" r="78" fill="none" stroke={accent} strokeWidth="6" />
          <polygon points="100,62 122,128 184,128 134,166 154,228 100,190 46,228 66,166 16,128 78,128" fill={accent} stroke={ink} strokeWidth="3" strokeLinejoin="miter" transform="scale(0.78) translate(28 30)" />
        </svg>
      );
    case 'amnesiac':
      // Bold question mark in disc.
      return (
        <svg {...c}>
          <circle cx="100" cy="130" r="78" fill={accent} stroke={ink} strokeWidth="4" />
          <path d="M76 102 Q76 76 100 76 Q124 76 124 100 Q124 118 108 124 Q100 128 100 144 L100 158" fill="none" stroke={ink} strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="100" cy="186" r="9" fill={ink} />
        </svg>
      );
    case 'plaguebearer':
      // Plague doctor beak mask.
      return (
        <svg {...c}>
          <path d="M60 78 Q100 50 140 78 L162 132 Q120 152 100 226 Q80 152 38 132 Z" fill={accent} stroke={ink} strokeWidth="4" strokeLinejoin="round" />
          <circle cx="80" cy="106" r="10" fill={ink} />
          <circle cx="120" cy="106" r="10" fill={ink} />
          <circle cx="80" cy="106" r="3" fill={accent} />
          <circle cx="120" cy="106" r="3" fill={accent} />
          <line x1="100" y1="160" x2="100" y2="220" stroke={ink} strokeWidth="3" />
        </svg>
      );
    case 'vampire':
      // Bat silhouette with fang.
      return (
        <svg {...c}>
          <path d="M20 132 L60 100 L80 116 Q100 92 120 116 L140 100 L180 132 L156 152 L130 142 L114 178 L100 162 L86 178 L70 142 L44 152 Z" fill={accent} stroke={ink} strokeWidth="4" strokeLinejoin="round" />
          <path d="M84 138 L96 168 L94 138 Z" fill={ink} />
          <path d="M116 138 L104 168 L106 138 Z" fill={ink} />
        </svg>
      );

    default: {
      // Generic fallback: a monogram disc using the role's number/initial.
      const label = (role?.number ?? id.slice(0, 2) ?? '?').toUpperCase();
      return (
        <svg {...c}>
          <circle cx="100" cy="130" r="78" fill="none" stroke={accent} strokeWidth="6" />
          <circle cx="100" cy="130" r="58" fill={accent} opacity="0.18" />
          <text
            x="100" y="156"
            textAnchor="middle"
            fontFamily="Big Shoulders Stencil, Big Shoulders Display, Impact, sans-serif"
            fontWeight="900"
            fontSize="86"
            fill={accent}
            letterSpacing="-2"
          >
            {label}
          </text>
        </svg>
      );
    }
  }
}
